"use client";

/**
 * Client session hook for V2's self-hosted auth.
 *
 * Calls V2's OWN route handlers, served at `/api/auth/...` (origin-relative).
 * Post-W4 cutover the app serves at the root domain (no basePath), so raw
 * `fetch("/api/auth/...")` hits V2's own route handlers directly.
 */
import { useCallback, useEffect, useState } from "react";
import { setCacheOwner, invalidateApiCache } from "@/lib/chitravan-api";

export interface SessionUser {
	id: string;
	email: string;
	role: string;
	name?: string | null;
	picture?: string | null;
}

// V2's own auth routes are served at the origin root (no basePath post-cutover).
const AUTH_BASE = "/api/auth";

export type SessionStatus = "loading" | "authenticated" | "unauthenticated";

export interface UseSessionResult {
	user: SessionUser | null;
	status: SessionStatus;
	refresh: () => Promise<void>;
}

export function useSession(): UseSessionResult {
	const [user, setUser] = useState<SessionUser | null>(null);
	const [status, setStatus] = useState<SessionStatus>("loading");

	const refresh = useCallback(async () => {
		try {
			const res = await fetch(`${AUTH_BASE}/session`, {
				credentials: "include",
				cache: "no-store",
			});
			if (!res.ok) {
				setCacheOwner(null); // SECURITY: unknown user → purge any cached responses
				setUser(null);
				setStatus("unauthenticated");
				return;
			}
			const data = (await res.json()) as { user: SessionUser | null };
			// SECURITY: reconcile the per-user cache owner BEFORE components read
			// cached data. If the session user changed (account switch / re-login),
			// this purges the previous user's cached GET responses.
			setCacheOwner(data.user?.id ?? null);
			setUser(data.user ?? null);
			setStatus(data.user ? "authenticated" : "unauthenticated");
		} catch {
			setCacheOwner(null);
			setUser(null);
			setStatus("unauthenticated");
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	return { user, status, refresh };
}

/**
 * Best-effort sign-out: hits V2's logout route (which blocklists the token on
 * FastAPI and clears the cookie), then redirects to the login page.
 */
export async function signOut(redirectTo: string = "/login"): Promise<void> {
	try {
		await fetch(`${AUTH_BASE}/logout`, {
			method: "POST",
			credentials: "include",
		});
	} catch {
		// Network failure — cookie may persist until expiry, but proceed to
		// redirect so the user is at least bounced out of the app UI.
	}
	if (typeof window !== "undefined") {
		// SECURITY: wipe this user's cached responses so the next login in this
		// browser can never render the previous account's data.
		setCacheOwner(null);
		invalidateApiCache();
		// redirectTo is an origin-relative path (e.g. "/login"). No basePath
		// post-cutover, so assign it directly.
		window.location.href = redirectTo;
	}
}
